import { useNavData } from '@/hooks/useNavData'

/*
  Figma: node 183:232 (footer)
  - bg: black (#000000), px: 24px, py: 48px, gap: 48px
  - Annotation: "Instagram & Facebook links come from IIA > Globals > Social Links"
  - Task: Glossary link sits below T&Cs (Teamwork task 26100629)
  - Task: Include 'Built by Combinate. Powered by Insites' message

  All styling lives in src/styles/components/footer.css. Font properties
  on <p>/<a> use an #app-prefixed selector there to beat the legacy reset
  in globals.css.
*/
export default function Footer() {
  const year = new Date().getFullYear()
  const navData = useNavData()

  // Only render social links if a URL has been set in IIA > Globals > Social Links.
  // Empty strings mean the field is unset, so the link should be hidden entirely.
  const instagramUrl = navData.social?.instagram?.trim() || ''
  const facebookUrl = navData.social?.facebook?.trim() || ''
  const hasSocialLinks = instagramUrl || facebookUrl

  // Contact email from IIA > Globals (company_email_1). Empty string = unset,
  // so the contact line is hidden entirely (same convention as social links).
  const contactEmail = navData.company_email?.trim() || ''

  return (
    <footer className="mig-footer">
      {/* Row 1: Acknowledgment + link columns */}
      <div className="mig-footer-row1">
        {/* Left column: acknowledgment + contact email, stacked. Wrapping keeps
            row 1 to three flex children so the desktop space-between layout
            holds; on mobile this column carries order:1 (see globals.css). */}
        <div className="mig-footer-ack-col">
          {/* Body/large-reg - Poppins Regular 24px, lineHeight 1.3, max-width 760px */}
          <p className="mig-footer-acknowledgment">
            We acknowledge that we operate on the lands, waters and skies of many First Nations Peoples.
            Migration Galleries share the responsibility of advocating for Country and honouring First
            Nations Peoples and knowledges.
          </p>

          {/* Contact email from IIA > Globals (company_email_1). Only renders
              when set, mirroring the social-link behaviour. */}
          {contactEmail && (
            <p className="mig-footer-contact-email">
              <a
                href={`mailto:${contactEmail}`}
                className="mig-footer-contact-email-link"
              >
                {contactEmail}
              </a>
            </p>
          )}
        </div>

        {/* Legal links */}
        <div className="mig-footer-links mig-footer-legal-links">
          <a href="/privacy-policy" className="mig-footer-link">Privacy Policy</a>
          <a href="/terms-and-conditions" className="mig-footer-link">T & C's</a>
          <a href="/glossary" className="mig-footer-link">Glossary</a>
          <a href="/contact" className="mig-footer-link">Contact Us</a>
          <a href="/faq" className="mig-footer-link">FAQs</a>
        </div>

        {/* Social links - from IIA Globals. Each link only renders if its URL has been populated. */}
        {hasSocialLinks && (
          <div className="mig-footer-links mig-footer-social-links">
            {instagramUrl && (
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="mig-footer-link">Instagram</a>
            )}
            {facebookUrl && (
              <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="mig-footer-link">Facebook</a>
            )}
          </div>
        )}
      </div>

      {/* Row 2: Copyright + production (left, stacked) | Built by (right, bottom-aligned).
          Matches Figma node 845:1424 — single 20px Poppins Regular row. */}
      <div className="mig-footer-row3">
        <div className="mig-footer-copyright-block">
          <p className="mig-footer-copyright">
            &copy; Migration Galleries {year}.
          </p>
          <p className="mig-footer-production">
            MG is a production of Eastern Suburbs Art Group, MG is a production of Willowcharm Pty Ltd.
          </p>
        </div>

        <p className="mig-footer-credit">
          Built by{' '}
          <a href="https://www.combinate.me/" target="_blank" rel="noopener noreferrer">
            Combinate
          </a>
          . Powered by{' '}
          <a href="https://www.insites.io/" target="_blank" rel="noopener noreferrer">
            Insites
          </a>
          .
        </p>
      </div>
    </footer>
  )
}
